import { useState } from "react";

import { trackQuizStep, trackQuizStart, trackFormSubmission } from "@/lib/tracking";

const Icon = ({ path, className = "h-4 w-4" }: { path: string; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={path} />
  </svg>
);

const CHEVRON_RIGHT = "m9 18 6-6-6-6";
const CHEVRON_LEFT = "m15 18-6-6 6-6";
const CHECK_CIRCLE = "M22 11.08V12a10 10 0 1 1-5.93-9.14M9 11l3 3L22 4";
const LOADER = "M21 12a9 9 0 1 1-6.22-8.56";

const QUIZ_STEPS = [
  {
    id: "service",
    question: "What type of project are you planning?",
    options: [
      { label: "Kitchen Remodeling", emoji: "🍳" },
      { label: "Bathroom Remodeling", emoji: "🛁" },
      { label: "Basement Finishing", emoji: "🏠" },
      { label: "Deck Construction", emoji: "🪵" },
      { label: "Roofing & Siding", emoji: "🏗️" },
      { label: "Other", emoji: "📋" },
    ],
  },
  {
    id: "timeline",
    question: "When are you looking to start?",
    options: [
      { label: "As soon as possible", emoji: "⚡" },
      { label: "Within 1-3 months", emoji: "📅" },
      { label: "3-6 months", emoji: "🗓️" },
      { label: "Just exploring options", emoji: "🔍" },
    ],
  },
  {
    id: "homeType",
    question: "What type of home do you have?",
    options: [
      { label: "Single Family Home", emoji: "🏡" },
      { label: "Townhouse", emoji: "🏘️" },
      { label: "Condo", emoji: "🏢" },
      { label: "Multi-Family", emoji: "🏗️" },
    ],
  },
  {
    id: "budget",
    question: "What's your estimated budget?",
    options: [
      { label: "Under $10,000", emoji: "💵" },
      { label: "$10,000 - $25,000", emoji: "💰" },
      { label: "$25,000 - $50,000", emoji: "💎" },
      { label: "$50,000+", emoji: "🏆" },
      { label: "Not sure yet", emoji: "🤔" },
    ],
  },
];

interface QuizFormProps {
  standalone?: boolean;
  compact?: boolean;
}

const QuizForm = ({ standalone, compact }: QuizFormProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [contactInfo, setContactInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    zipCode: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const totalSteps = QUIZ_STEPS.length + 1;
  const isContactStep = currentStep === QUIZ_STEPS.length;

  const selectOption = (option: string) => {
    if (currentStep === 0 && Object.keys(answers).length === 0) {
      trackQuizStart();
    }
    const step = QUIZ_STEPS[currentStep];
    setAnswers((prev) => ({ ...prev, [step.id]: option }));
    trackQuizStep(currentStep + 1, step.id, option, step.question);
    setTimeout(() => setCurrentStep((s) => s + 1), 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submission = {
      ...contactInfo,
      service: answers.service || "",
      timeline: answers.timeline || "",
      homeType: answers.homeType || "",
      budget: answers.budget || "",
    };

    setSubmitting(true);
    setSubmitted(true);
    setSubmitting(false);

    window.setTimeout(() => {
      try {
        trackFormSubmission(submission);
      } catch (err) {
        console.warn("[QuizForm] lead tracking failed", err);
      }
    }, 0);
  };

  if (submitted) {
    return (
      <div className={`quiz-card text-center ${compact ? "!p-4" : ""}`}>
        <Icon path={CHECK_CIRCLE} className="mx-auto mb-4 h-16 w-16 text-primary" />
        <h3 className="mb-2 font-heading text-2xl font-bold text-foreground">You're All Set!</h3>
        <p className="mb-4 text-muted-foreground">
          We'll reach out within 24 hours with your free estimate. In the meantime, feel free to call us!
        </p>
        <a href="tel:2405872207" className="cta-button">
          Call (240) 587-2207
        </a>
      </div>
    );
  }

  return (
    <div className={`quiz-card ${compact ? "!p-4" : ""}`}>
      {!compact && (
        <div className="mb-6 rounded-xl bg-destructive px-6 py-4 text-center">
          <p className="font-heading text-lg font-bold text-primary-foreground md:text-xl">
            ⚡ Free Estimate in 30 Seconds
          </p>
          <p className="text-sm text-primary-foreground/80">Answer {QUIZ_STEPS.length} quick questions, no obligation</p>
        </div>
      )}

      {/* Progress */}
      <div className={compact ? "mb-4" : "mb-6"}>
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-muted-foreground">
            Question {currentStep + 1} of {totalSteps}
          </span>
          <span className="font-semibold text-primary">
            {Math.round(((currentStep + 1) / totalSteps) * 100)}%
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {!isContactStep ? (
        <>
          <h3 className={`font-heading font-bold text-foreground ${compact ? "mb-3 text-base" : "mb-5 text-xl"}`}>
            {QUIZ_STEPS[currentStep].question}
          </h3>
          <div className={`grid ${compact ? "gap-2" : "gap-3"}`}>
            {QUIZ_STEPS[currentStep].options.map(({ label, emoji }) => (
              <button
                key={label}
                onClick={() => selectOption(label)}
                className={`quiz-option flex items-center gap-3 text-left ${compact ? "!p-2.5 text-sm" : ""} ${answers[QUIZ_STEPS[currentStep].id] === label ? "selected" : ""}`}
              >
                <span className={compact ? "text-base" : "text-xl"}>{emoji}</span>
                <span>{label}</span>
                <Icon path={CHEVRON_RIGHT} className="ml-auto h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep((s) => s - 1)}
              className="mt-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <Icon path={CHEVRON_LEFT} /> Back
            </button>
          )}
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <h3 className="mb-1 font-heading text-xl font-bold text-foreground">
            Almost done! Where should we send your estimate?
          </h3>
          <p className="mb-5 text-sm text-muted-foreground">
            We'll never share your information or spam you.
          </p>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                required
                placeholder="First Name"
                value={contactInfo.firstName}
                onChange={(e) => setContactInfo((p) => ({ ...p, firstName: e.target.value }))}
                className="rounded-lg border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                required
                placeholder="Last Name"
                value={contactInfo.lastName}
                onChange={(e) => setContactInfo((p) => ({ ...p, lastName: e.target.value }))}
                className="rounded-lg border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <input
              required
              type="email"
              placeholder="Email Address"
              value={contactInfo.email}
              onChange={(e) => setContactInfo((p) => ({ ...p, email: e.target.value }))}
              className="rounded-lg border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              required
              type="tel"
              placeholder="Phone Number"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo((p) => ({ ...p, phone: e.target.value }))}
              className="rounded-lg border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              required
              placeholder="Zip Code"
              value={contactInfo.zipCode}
              onChange={(e) => setContactInfo((p) => ({ ...p, zipCode: e.target.value }))}
              className="rounded-lg border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="cta-button mt-5 w-full justify-center disabled:opacity-70"
          >
            {submitting ? (
              <><Icon path={LOADER} className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>
            ) : (
              "Get My Free Estimate →"
            )}
          </button>
          <button
            type="button"
            onClick={() => setCurrentStep((s) => s - 1)}
            className="mt-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <Icon path={CHEVRON_LEFT} /> Back
          </button>
        </form>
      )}
    </div>
  );
};

export default QuizForm;
